/**
 * kullaniciTalebiService.js — Kullanıcı Ekleme Talepleri, the company's side (#613; backend #612, KullaniciTalebiController, V136).
 *
 * A Katılımcı or Dekoratör company asks SAKA for a new login from Firma Kartı > Company Representatives > Users; staff decide (#614).
 * Base: {JAVA_BACKEND_API_URL}/firma/{firmaId}/kullanici-talepleri — FIRMA_PROFILI READ to read, WRITE to send / withdraw; staff pass the
 * tenancy check but cannot send. No mock: the answer carries the texts (status names, the reason a request cannot be sent) in the request
 * language, so the page prints them as they come. The staff calls (#614) are at the bottom of the file.
 *
 *   GET  → { talepler: [row], bekleyenSayisi, bekleyenSiniri, talepEdebilir, engel, roller: [{ portalRolId, kod, ad, varsayilan }] }
 *          row: { id, ad, soyad, eposta, telefon, unvan, kullaniciAdi, portalRolId, portalRolAdi, durum 0-3 (Bekliyor / Onaylandı /
 *          Reddedildi / Geri çekildi), durumKodu, durumAdi, redSebebi, talepEdenAdi, olusturulanKullaniciAdi, olusturmaTarihi, kararTarihi }
 *          `engel` = why the caller cannot send now (null when it can); `talepEdebilir` = engel is null.
 *   POST { ad, soyad, eposta, kullaniciAdi, telefon?, unvan?, portalRolId? } → 201 { talep, mesaj, epostaKullanimda, uyari }
 *          uyari = the address is already on another login (the company may still send; SAKA decides).
 *   PUT  /{id}/geri-cek → { talep, mesaj }
 * Refusals (400 a field, 403 the guards, 404, 409 the cap / a login name taken / not pending) carry their text in `message`.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { currentLanguageId } from '../i18n';

const url = (firmaId) => `${JAVA_BACKEND_API_URL}/firma/${firmaId}/kullanici-talepleri`;

/** durum numbers, as the backend sends them. */
export const TALEP_DURUMU = { BEKLIYOR: 0, ONAYLANDI: 1, REDDEDILDI: 2, GERI_CEKILDI: 3 };

/** The backend's own text of a refusal (it answers in the request language), else the transport's message. */
export function talepHatasi(error) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object' && data.message) return data.message;
  return error?.message || '';
}

/** The company's requests and what the form needs. */
export const getKullaniciTalepleri = (firmaId, { languageId = currentLanguageId() } = {}) =>
  axios.get(url(firmaId), { params: { languageId }, headers: getAuthHeader() }).then((r) => r.data);

/** Sends one request. `form` = { ad, soyad, eposta, kullaniciAdi, telefon, unvan, portalRolId }; empty optional fields are left out. */
export function gonderKullaniciTalebi(firmaId, form, { languageId = currentLanguageId() } = {}) {
  const body = {
    ad: (form.ad || '').trim(),
    soyad: (form.soyad || '').trim(),
    eposta: (form.eposta || '').trim(),
    kullaniciAdi: (form.kullaniciAdi || '').trim(),
  };
  const telefon = (form.telefon || '').trim();
  const unvan = (form.unvan || '').trim();
  if (telefon) body.telefon = telefon;
  if (unvan) body.unvan = unvan;
  if (form.portalRolId != null && form.portalRolId !== '') body.portalRolId = Number(form.portalRolId);
  return axios.post(url(firmaId), body, { params: { languageId }, headers: getAuthHeader() }).then((r) => r.data);
}

/** Takes a pending request back. */
export const geriCekKullaniciTalebi = (firmaId, talepId, { languageId = currentLanguageId() } = {}) =>
  axios.put(`${url(firmaId)}/${talepId}/geri-cek`, null, { params: { languageId }, headers: getAuthHeader() }).then((r) => r.data);

// ─── Staff side (#614): the review list, one request for the approval window, approve, reject ───────────────────────────────────
// Base: {JAVA_BACKEND_API_URL}/kullanici-talepleri — every call needs kullanici.olustur (Admin, Kayıt Admin / Moderatör, Saha Admin /
// Moderatör; root passes), the backend answers 403 otherwise.
//   GET  ?durum&firmaId&fuarId&q&page&size → { sayilar: { bekliyor, onaylandi, reddedildi, geriCekildi, toplam }, talepler: [row], toplam, page, size }
//        a staff row adds firmaAdi, talepEdenKullaniciAdi, kararVerenAdi, epostaKullananSayisi (active logins holding the address) to the
//        company's row; `sayilar` counts every state under the other filters; pending first.
//   GET  /bekleyen-sayisi?firmaId → { bekleyen }
//   GET  /{id} → { talep, epostaKullananlar: [{ userId, userLoginName, userName, userSurname, userActive, userLastLogin, personel, firmalar,
//          pasifYapilabilir }], kullaniciAdiAlinmis, kullaniciAdiBaskaTalepte, roller, varsayilanUserGroupId, userGroupIds, gecmis }
//   POST /{id}/onayla { kullaniciAdi, ad, soyad, eposta, telefon, unvan, portalRolId, userGroupId, pasifYapilacakKullaniciIds, aktivasyonMaili }
//        → { talep, kullanici: { userId, userLoginName }, pasifYapilanlar, mesaj, aktivasyonMaili: { sent, to, message, ... } | null }
//        ONE call creates the login, links it to the company, gives the portal role, makes the ticked logins passive and closes the request;
//        the activation mail goes out after the commit (a mail that fails leaves the login as it is).
//   POST /{id}/reddet { redSebebi } → { talep, mesaj }
const STAFF = `${JAVA_BACKEND_API_URL}/kullanici-talepleri`;

/** Filter values that mean "all" (empty, null, NaN) are left out of the query. */
const doluParametreler = (p) => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== '' && v != null && !Number.isNaN(v)));

/** { durum 0-3, firmaId, fuarId, q, page (0-based), size } — every key optional. */
export const getKullaniciTalepListesi = (filtre = {}, { languageId = currentLanguageId() } = {}) =>
  axios.get(STAFF, { params: doluParametreler({ ...filtre, languageId }), headers: getAuthHeader() }).then((r) => r.data);

/** Pending requests of one company ({ firmaId }) or of all ({ firmaId } left out). */
export const getBekleyenKullaniciTalepSayisi = (firmaId) =>
  axios.get(`${STAFF}/bekleyen-sayisi`, { params: doluParametreler({ firmaId }), headers: getAuthHeader() })
    .then((r) => Number(r.data?.bekleyen) || 0);

export const getKullaniciTalebiDetay = (talepId, { languageId = currentLanguageId() } = {}) =>
  axios.get(`${STAFF}/${talepId}`, { params: { languageId }, headers: getAuthHeader() }).then((r) => r.data);

/** `body` as above; empty optional text fields go as "" (the backend reads blank as none). */
export const onaylaKullaniciTalebi = (talepId, body, { languageId = currentLanguageId() } = {}) =>
  axios.post(`${STAFF}/${talepId}/onayla`, body, { params: { languageId }, headers: getAuthHeader() }).then((r) => r.data);

export const reddetKullaniciTalebi = (talepId, redSebebi, { languageId = currentLanguageId() } = {}) =>
  axios.post(`${STAFF}/${talepId}/reddet`, { redSebebi: (redSebebi || '').trim() }, { params: { languageId }, headers: getAuthHeader() })
    .then((r) => r.data);
